<script setup>
import InvoicesTable from '../../components/table/Invoices';
</script>

<template>
  <div data-view="invoices">
    <router-view />

    <layout-logged-frame :title="$t('invoices')">
      <template v-slot:content>
        <InvoiceFilters
          @filter="onFilter"
          @clearFilters="onClearFilters"
        />

        <InvoicesTable
          :items="invoices"
          :pagination="pagination"
          :is-loading="isLoading"
          :pages="totalPages"
          :on-select-item="selectInvoice"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
        />
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import InvoiceFilters from '../../components/forms/filters/Invoice';
  import { isStr, extractNestedProp } from '../../utils';
  import columns from '../../config/invoices/columns';

  export default {
    components: {
      InvoiceFilters,
    },
    data() {
      return {
        columns,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: 'created_at',
          totalItems: 0,
          search: '',
        },
        totalPages: 0,
        query: { per_page: 15 },
        isLoading: false,
      };
    },
    computed: {
      invoices() {
        return this.$store.getters['invoices/items'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      currentRoute() {
        return this.$route;
      },
    },
    watch: {
      currentRoute(to, from) {
        if (to.query?.status === 'success' && from.name === 'invoices-edit' && to.name === 'invoices') {
          this.getInvoices(this.query);
        }
      },
    },
    methods: {
      getInvoices(query = {}) {
        this.isLoading = true;
        this.pagination.order = query.order;

        return this.$store
          .dispatch('invoices/getItems', query)
          .then((res) => {
            const { itemsMeta } = this.$store.getters['invoices/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      selectInvoice(invoice) {
        this.$store.dispatch('invoices/setActiveItem', invoice);
        this.$router.push({ name: 'invoices-edit', params: { id: invoice.id } });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;
        this.getInvoices(nextQuery);
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;
        this.getInvoices(nextQuery);
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          ...filters,
          page: 1,
        };

        this.getInvoices(this.query);
      },
      onClearFilters() {
        const { sortBy, order } = this.pagination;
        this.query = { sort: sortBy, order, per_page: 15, page: 1 };
        this.getInvoices(this.query);
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
      onAction({ id, invoice_number }, action) {
        const query = { id, invoice_number };

        if (action === 'pdf') {
          return this.$store.dispatch('invoices/generatePdf', query);
        }
      },
    },
  };
</script>

<style lang="scss">
@use '../../assets/styles/theme' as *;

[data-view='invoices'] {
  .trigger.toggle-menu {
    margin-right: 0;
  }

  .trigger.download-pdf {
    display: flex;
    justify-content: flex-start;
    align-items: center;

    .v-icon {
      margin: 0 6px 0 0;
    }
  }

  .v-datatable {
    min-width: $table-min-width;
  }
}
</style>
